// src/decodeCatalogModel.ts
import { isMeaningfulBrandName } from "@rock/util";

// src/decodeModelNumber.ts
import { normalizeBrandName, normalizeIdentifier } from "@rock/util";

// src/brands/whirlpoolCorp.ts
var LAUNDRY_TAIL = /^\d{3}/;
var LAUNDRY_COMBO_TAIL = /^\d{3}[\dC]/;
var COOKING_TAIL = /^[0-9A]\d{2}[A-Z]/;
var COOKING_COMBO_COLLISION_TAIL = /^[0-9A]\d{2}[A-Z]\d/;
var ANY_TAIL = /^/;
var WHIRLPOOL = [
  { code: "TW", type: "Top load washer", category: "laundry", tail: LAUNDRY_TAIL },
  { code: "FW", type: "Front load washer", category: "laundry", tail: LAUNDRY_TAIL },
  { code: "FC", type: "Front load washer-dryer combo", category: "laundry", tail: LAUNDRY_COMBO_TAIL },
  { code: "ED", type: "Electric dryer", category: "laundry", tail: LAUNDRY_TAIL },
  { code: "GD", type: "Gas dryer", category: "laundry", tail: LAUNDRY_TAIL },
  { code: "HD", type: "Heat pump dryer", category: "laundry", tail: LAUNDRY_TAIL },
  { code: "CD", type: "Condensing dryer", category: "laundry", tail: LAUNDRY_TAIL },
  // Refrigeration: platform `R` (freestanding refrigerator) or `Z` (freestanding freezer), then the
  // door configuration. `M`/`X` and `Q` both land on French door in Whirlpool's own catalog.
  { code: "RF", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RM", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RX", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  // `Q` (quad-door) and `V` (5-door FDBM) are DOOR COUNTS, and Whirlpool's own catalog files both under
  // French Door — `WRQ651SNLZ`, `WRQA59CNKZ` and `WRQC7836RZ` all come back `…@French Door`. The
  // configuration table tempted a "Quad-door refrigerator" type here; the catalog is the better
  // authority on what the product IS, and `FDBM` expands to French Door Bottom Mount besides.
  { code: "RQ", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RV", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RS", type: "Side-by-side refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RB", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RT", type: "Top mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RU", type: "Single door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "ZF", type: "Upright freezer", category: "refrigeration", tail: ANY_TAIL },
  // Cooking: platform (E slide-in / F freestanding / G double oven) × sub-platform (C coil / E electric
  // / G gas). `FC` collides with the laundry combo above — `COOKING_TAIL` is what separates them.
  { code: "FG", type: "Freestanding gas range", category: "cooking", tail: COOKING_TAIL },
  { code: "FE", type: "Freestanding electric range", category: "cooking", tail: COOKING_TAIL },
  { code: "FC", type: "Freestanding coil electric range", category: "cooking", tail: COOKING_COMBO_COLLISION_TAIL },
  { code: "EG", type: "Slide-in gas range", category: "cooking", tail: COOKING_TAIL },
  { code: "EE", type: "Slide-in electric range", category: "cooking", tail: COOKING_TAIL },
  { code: "GG", type: "Double oven gas range", category: "cooking", tail: COOKING_TAIL },
  { code: "GE", type: "Double oven electric range", category: "cooking", tail: COOKING_TAIL },
  { code: "OS", type: "Single built-in oven", category: "cooking", tail: ANY_TAIL },
  { code: "OD", type: "Double built-in oven", category: "cooking", tail: ANY_TAIL },
  { code: "MH", type: "Over-the-range microwave", category: "cooking", tail: ANY_TAIL },
  { code: "MC", type: "Countertop microwave", category: "cooking", tail: ANY_TAIL },
  // Dishwashers: platform `D`, then the control/handle configuration. Whirlpool's own `P` means BOTH
  // "pocket handle top control" and "portable", and its catalog files WDP models under both — so the
  // type stays at the level the code actually determines.
  { code: "DT", type: "Dishwasher", category: "dishwashing", tail: ANY_TAIL },
  { code: "DF", type: "Dishwasher", category: "dishwashing", tail: ANY_TAIL },
  { code: "DP", type: "Dishwasher", category: "dishwashing", tail: ANY_TAIL }
];
var MAYTAG = [
  { code: "VW", type: "Top load washer", category: "laundry", tail: ANY_TAIL },
  { code: "HW", type: "Front load washer", category: "laundry", tail: ANY_TAIL },
  { code: "ED", type: "Electric dryer", category: "laundry", tail: ANY_TAIL },
  { code: "GD", type: "Gas dryer", category: "laundry", tail: ANY_TAIL },
  { code: "ER", type: "Freestanding electric range", category: "cooking", tail: ANY_TAIL },
  { code: "GR", type: "Freestanding gas range", category: "cooking", tail: ANY_TAIL },
  { code: "ES", type: "Slide-in electric range", category: "cooking", tail: ANY_TAIL },
  { code: "GS", type: "Slide-in gas range", category: "cooking", tail: ANY_TAIL },
  { code: "ET", type: "Double oven electric range", category: "cooking", tail: ANY_TAIL },
  { code: "GT", type: "Double oven gas range", category: "cooking", tail: ANY_TAIL },
  { code: "DB", type: "Dishwasher", category: "dishwashing", tail: ANY_TAIL },
  // Refrigeration is TWO characters — platform then the ice/depth line letter — even though the
  // platform alone determines the product. A bare `F`/`B`/`S` would be unanchored: Maytag has no `R`
  // or `Z` in position 2 the way Whirlpool does, so `MFW9600SQ` (an Epic FRONT-LOAD WASHER) and
  // `MBE22` both decoded as refrigerators, at `high` confidence. That was survivable while the type
  // was advisory; it is not, now that a decoded type can refuse a document.
  //
  // A tail-shape test cannot substitute here — `MFI2570FEZ` and `MFW9600SQ` are the same shape — so
  // the line letters are enumerated, and a code outside them stays silent.
  { code: "FC", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "FF", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "FI", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "FT", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "BC", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "BF", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "BI", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "BT", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "SB", type: "Side-by-side refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "SC", type: "Side-by-side refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "SS", type: "Side-by-side refrigerator", category: "refrigeration", tail: ANY_TAIL }
];
var AMANA_LAUNDRY = [
  { code: "TW", type: "Top load washer", category: "laundry", tail: ANY_TAIL },
  { code: "FW", type: "Front load washer", category: "laundry", tail: ANY_TAIL },
  { code: "ED", type: "Electric dryer", category: "laundry", tail: ANY_TAIL },
  { code: "GD", type: "Gas dryer", category: "laundry", tail: ANY_TAIL }
];
var AMANA_REFRIGERATION = [
  { code: "RT", type: "Top mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RB", type: "Bottom mount refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RF", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RM", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RV", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RQ", type: "French door refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RS", type: "Side-by-side refrigerator", category: "refrigeration", tail: ANY_TAIL },
  { code: "RU", type: "Single door refrigerator", category: "refrigeration", tail: ANY_TAIL }
];
var WHIRLPOOL_CORP_BRANDS = [
  { scheme: "whirlpool-corp", letter: "W", brand: "Whirlpool", names: ["WHIRLPOOL"], rules: WHIRLPOOL },
  {
    scheme: "whirlpool-corp",
    letter: "M",
    brand: "Maytag",
    names: ["MAYTAG", "MAYTAGAPPLIANCES"],
    rules: MAYTAG
  },
  // Amana owns two letters — see `findScheme`, which matches on brand name AND leading character.
  {
    scheme: "whirlpool-corp",
    letter: "N",
    brand: "Amana",
    names: ["AMANA", "AMANAAPPLIANCES"],
    rules: AMANA_LAUNDRY
  },
  {
    scheme: "whirlpool-corp",
    letter: "A",
    brand: "Amana",
    names: ["AMANA", "AMANAAPPLIANCES"],
    rules: AMANA_REFRIGERATION
  }
];
function whirlpoolSegments(scheme, rule, canonical) {
  const remainder = canonical.slice(1 + rule.code.length);
  const segments = [
    { value: scheme.letter, label: "Brand", meaning: scheme.brand },
    { value: rule.code, label: "Platform", meaning: rule.type }
  ];
  if (remainder) segments.push({ value: remainder, label: "Series, features and colour" });
  return segments;
}

// src/decodeModelNumber.ts
var MIN_DECODABLE_LENGTH = 6;
function decodeModelNumber(input) {
  const canonical = normalizeIdentifier(input.modelNumber);
  const scheme = findScheme(input.brandNames, canonical);
  if (!scheme || canonical.length < MIN_DECODABLE_LENGTH) return void 0;
  const claimed = scheme.rules.filter((rule) => canonical.startsWith(rule.code, 1));
  const survivors = claimed.filter((rule) => rule.tail.test(canonical.slice(1 + rule.code.length)));
  const winner = survivors.length === 1 ? survivors[0] : void 0;
  if (!winner) return void 0;
  const notes = collisionNote(claimed, winner);
  return {
    brand: scheme.brand,
    type: winner.type,
    category: winner.category,
    confidence: notes.length ? "medium" : "high",
    segments: whirlpoolSegments(scheme, winner, canonical),
    notes,
    scheme: scheme.scheme
  };
}
function findScheme(brandNames, canonical) {
  const folded = brandNames.map((name) => normalizeBrandName(name).key).filter(Boolean);
  return WHIRLPOOL_CORP_BRANDS.find(
    (scheme) => canonical.startsWith(scheme.letter) && scheme.names.some((name) => folded.includes(name))
  );
}
function collisionNote(claimed, winner) {
  const others = claimed.filter((rule) => rule !== winner).map((rule) => rule.type);
  if (!others.length) return [];
  return [
    `"${winner.code}" also denotes ${others.join(", ")} on this brand; the characters after it match this product\u2019s shape.`
  ];
}

// src/decodeCatalogModel.ts
function decodeCatalogModel(model, brand) {
  const brandNames = catalogBrandNames(model, brand);
  if (brandNames.length === 0) return void 0;
  return decodeModelNumber({ brandNames, modelNumber: model.modelCanonical });
}
function catalogBrandNames(model, brand) {
  if (model.brandId) {
    return [...new Set([brand?.canonicalName, ...brand?.aliases ?? []].filter(isMeaningfulBrandName))];
  }
  return isMeaningfulBrandName(model.details?.brand) ? [model.details.brand] : [];
}

// src/modelJoin.ts
import { ModelSpellingKeySchema, pendingBrandRequestOf } from "@rock/types";
import {
  cleanFirestoreId,
  confusionKey,
  identifierStrengthOf,
  typeAffinity
} from "@rock/util";
function buildSpellingKey(canonical) {
  return ModelSpellingKeySchema.decode(cleanFirestoreId(confusionKey(canonical)));
}
function buildProductNameKey(canonical) {
  return ModelSpellingKeySchema.decode(cleanFirestoreId(canonical));
}
function spellingKeyFor(keyedBy, canonical) {
  return keyedBy === "product-name" ? buildProductNameKey(canonical) : buildSpellingKey(canonical);
}
function joinBrandOf(brand) {
  return brand ? { kind: "brand", id: brand.id, reviewed: brand.reviewStatus === "reviewed" } : void 0;
}
function requestedJoinBrand(requestId) {
  return requestId ? { kind: "request", id: requestId } : void 0;
}
function joinCandidateOf(model, liveBrand) {
  const brand = model.brandId ? joinBrandOf(liveBrand) : requestedJoinBrand(pendingBrandRequestOf(model));
  return brand ? { model, brand } : { model };
}
function joinObservedModel(candidates, observed, families) {
  const judged = candidates.map((candidate) => judge(candidate, observed, families));
  const passing = judged.filter((entry) => entry.affinity && entry.brand !== "contradicts");
  if (passing.length === 0) return { kind: "mint", flags: mintFlags(judged, observed) };
  const live = passing.filter((entry) => !entry.candidate.model.archivedAt);
  const pool = live.length > 0 ? live : passing;
  const current = pool.find((entry) => entry.candidate.model.id === observed.currentModelId);
  if (current) return { kind: "join", model: current.candidate.model, flags: joinFlags(current) };
  if (live.length > 0 && !observed.brand) {
    const brandIds = [...new Set(live.flatMap((entry) => reviewedBrandIdOf(entry.candidate)))];
    if (brandIds.length > 1) return { kind: "ambiguous", brandIds };
  }
  const best = pool.reduce((first, next) => byPreference(first, next) <= 0 ? first : next);
  return { kind: "join", model: best.candidate.model, flags: joinFlags(best) };
}
function modelJoinFacts(candidate, unit, families) {
  return {
    affinity: typeAffinity(unit.typeId, [candidate.model.typeId], families),
    brand: brandRelation(candidate.brand, unit.brand)
  };
}
function modelJoinStrength(model) {
  return subjectStrength(model.modelCanonical, model.identity?.keyedBy);
}
function brandRelation(candidate, observed) {
  if (!candidate || !observed) return "open";
  return candidate.kind === observed.kind && candidate.id === observed.id ? "same" : "contradicts";
}
function brandTwinOf(model, others) {
  if (!model.brandId) return void 0;
  const key = spellingKeyFor(model.identity?.keyedBy, model.modelCanonical);
  const twins = others.filter(
    (other) => other.id !== model.id && !other.archivedAt && !other.mergedInto && other.brandId === model.brandId && other.typeId === model.typeId && spellingKeyFor(other.identity?.keyedBy, other.modelCanonical) === key
  );
  const byAge = [...twins].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return byAge.find((other) => other.modelCanonical === model.modelCanonical) ?? byAge[0];
}
function judge(candidate, observed, families) {
  return {
    candidate,
    ...modelJoinFacts(candidate, observed, families),
    exactSpelling: candidate.model.modelCanonical === observed.canonical
  };
}
var BRAND_PREFERENCE = { same: 0, open: 1, contradicts: 2 };
function candidateBrandRank(entry) {
  const brand = entry.candidate.brand;
  if (!brand) return 3;
  if (brand.kind === "request") return 2;
  return brand.reviewed ? 0 : 1;
}
function reviewedBrandIdOf({ brand }) {
  return brand?.kind === "brand" && brand.reviewed ? [brand.id] : [];
}
function byPreference(a, b) {
  return BRAND_PREFERENCE[a.brand] - BRAND_PREFERENCE[b.brand] || candidateBrandRank(a) - candidateBrandRank(b) || Number(a.affinity !== "type") - Number(b.affinity !== "type") || Number(!a.exactSpelling) - Number(!b.exactSpelling);
}
function joinFlags(entry) {
  return entry.affinity === "family" ? ["joined-via-family"] : [];
}
function mintFlags(judged, observed) {
  const strong = subjectStrength(observed.canonical, observed.keyedBy) === "strong";
  const otherType = judged.some((entry) => !entry.affinity && entry.brand !== "contradicts");
  return strong && otherType ? ["strong-number-other-type"] : [];
}
function subjectStrength(canonical, keyedBy) {
  return keyedBy === "product-name" ? "weak" : identifierStrengthOf(canonical);
}
function modelMergeBlockedReason(loser, winner, families, unitBrands) {
  const facts = modelJoinFacts(winner, { typeId: loser.model.typeId, brand: loser.brand }, families);
  if (!facts.affinity) return "The two models are filed under unrelated types. Change the type on one of them first.";
  if (facts.brand !== "contradicts") return void 0;
  const request = [loser.brand, winner.brand].find((brand) => brand?.kind === "request");
  if (request) return `One model waits on brand request ${request.id}. Resolve the request first.`;
  if (!unitBrands) {
    return "The two models carry different brands. Merge the brands, or fix the brand on one of them, first.";
  }
  const contradicting = unitBrands.filter((brand) => brandRelation(winner.brand, brand) === "contradicts").length;
  if (contradicting === 0) return void 0;
  const subject = contradicting === 1 ? "A unit on this model names" : `${contradicting} units on this model name`;
  return `${subject} a brand other than the survivor's, and an owner's brand is the truth. It stays apart until the owner changes it, or merge the brands if they are one company.`;
}
function mergeReplacesBrand(loser, winner) {
  return brandRelation(winner.brand, loser.brand) === "contradicts";
}
var MAX_MODEL_MERGE_HOPS = 3;
async function resolveMergedModel(model, load) {
  let current = model;
  for (let hops = 0; current?.mergedInto; hops++) {
    if (hops >= MAX_MODEL_MERGE_HOPS) return void 0;
    current = await load(current.mergedInto);
  }
  return current;
}
async function resolveMergedModels(models, load) {
  const resolved = await Promise.all(models.map((model) => model ? resolveMergedModel(model, load) : void 0));
  const unique = [];
  for (const model of resolved) {
    if (model && !unique.some((seen) => seen.id === model.id)) unique.push(model);
  }
  return unique;
}
export {
  MAX_MODEL_MERGE_HOPS,
  brandRelation,
  brandTwinOf,
  buildProductNameKey,
  buildSpellingKey,
  catalogBrandNames,
  decodeCatalogModel,
  decodeModelNumber,
  joinBrandOf,
  joinCandidateOf,
  joinObservedModel,
  mergeReplacesBrand,
  modelJoinFacts,
  modelJoinStrength,
  modelMergeBlockedReason,
  requestedJoinBrand,
  resolveMergedModel,
  resolveMergedModels,
  spellingKeyFor
};
//# sourceMappingURL=index.js.map